// 5-TIER SYSTEM
export type SubscriptionPlan = 
  | 'free'              // Free Trial (14 days)
  | 'pro_monthly'       // Pro Monthly (690 THB/month)
  | 'pro_yearly'        // Pro Yearly (6,900 THB/year)
  | 'pro_plus_monthly'  // Pro+ Monthly (1,590 THB/month)
  | 'pro_plus_yearly';  // Pro+ Yearly (15,900 THB/year)

export interface UserStorageData {
  plan: SubscriptionPlan;
  trialEndsAt?: any;
  storageLimitGB?: number;
  storageUsedBytes: number;
  addons?: {
    extraStorageGB: number;
    active: boolean;
  };
}

export interface FileMetadata {
  uid: string;
  key: string;
  path: string;
  sizeBytes: number;
  mimeType: string;
  createdAt: any;
  status: 'active' | 'deleted';
  bucket: string;
  publicUrl: string;
}

const PLAN_STORAGE_GB: Record<SubscriptionPlan, number> = {
  free: 5,                // Free Trial: 5 GB
  pro_monthly: 20,        // Pro Monthly: 20 GB (supports add-ons)
  pro_yearly: 25,         // Pro Yearly: 20 GB + 5 GB bonus = 25 GB (supports add-ons)
  pro_plus_monthly: 30,   // Pro+ Monthly: 30 GB (supports add-ons)
  pro_plus_yearly: 35,    // Pro+ Yearly: 30 GB + 5 GB bonus = 35 GB (supports add-ons)
};

export function getBasePlanGB(plan: SubscriptionPlan | string): number {
  // Handle legacy tier names
  const legacyMapping: Record<string, number> = {
    'pro': 20,           // Legacy "pro" -> 20 GB (treat as monthly)
    'yearly': 25,        // Legacy "yearly" -> 25 GB (Pro Yearly with bonus)
    'pro_plus': 30,      // Legacy "pro_plus" -> 30 GB (treat as monthly)
  };
  
  // Check if it's a legacy tier
  if (legacyMapping[plan as string]) {
    return legacyMapping[plan as string];
  }
  
  // Use standard mapping
  return PLAN_STORAGE_GB[plan as SubscriptionPlan] || PLAN_STORAGE_GB.free;
}

export function gbToBytes(gb: number): number {
  return gb * Math.pow(1024, 3);
}

export function bytesToGB(bytes: number): number {
  return bytes / Math.pow(1024, 3);
}

export function getStorageLimitBytes(userData: UserStorageData): number {
  const basePlanGB = getBasePlanGB(userData.plan);
  
  let addonGB = 0;
  if (userData.addons?.active && userData.plan !== 'free') {
    addonGB = userData.addons.extraStorageGB || 0;
  }
  
  const totalGB = basePlanGB + addonGB;
  return gbToBytes(totalGB);
}

export function getStorageLimitGB(userData: UserStorageData): number {
  const basePlanGB = getBasePlanGB(userData.plan);
  
  let addonGB = 0;
  if (userData.addons?.active && userData.plan !== 'free') {
    addonGB = userData.addons.extraStorageGB || 0;
  }
  
  return basePlanGB + addonGB;
}

export function canUpload(userData: UserStorageData, fileSizeBytes: number): boolean {
  const limitBytes = getStorageLimitBytes(userData);
  const usedBytes = userData.storageUsedBytes || 0;
  
  return (usedBytes + fileSizeBytes) <= limitBytes;
}

export function getStorageUsagePercent(userData: UserStorageData): number {
  const limitBytes = getStorageLimitBytes(userData);
  const usedBytes = userData.storageUsedBytes || 0;
  
  if (limitBytes === 0) return 0;
  
  return Math.min((usedBytes / limitBytes) * 100, 100);
}

export function isStorageNearLimit(userData: UserStorageData, threshold: number = 80): boolean {
  return getStorageUsagePercent(userData) >= threshold;
}

export function isStorageOverLimit(userData: UserStorageData): boolean {
  const limitBytes = getStorageLimitBytes(userData);
  const usedBytes = userData.storageUsedBytes || 0;
  
  return usedBytes > limitBytes;
}

export function canUseAddons(plan: SubscriptionPlan): boolean {
  return plan !== 'free'; // All paid plans support add-ons
}

export const ADDON_OPTIONS = [
  { gb: 5, price: 99, label: '+5 GB' },
  { gb: 10, price: 199, label: '+10 GB' },
  { gb: 25, price: 490, label: '+25 GB' },
  { gb: 50, price: 990, label: '+50 GB' },
  { gb: 100, price: 1790, label: '+100 GB' },
  { gb: 200, price: 2990, label: '+200 GB' },
] as const;
